/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection } from 'geojson';
import { handleResponse } from '@opengeoweb/api';
import { authFetch } from '@opengeoweb/authentication';
import type {
  Concept,
  DataExplorerPresets,
  DiscoveryMetadataFeature,
  DiscoveryMetadataItems,
  Queryable,
} from '../../store/types';

export const LIMIT = 100;
export const MAX_PAGES = 100;

interface FetchPageOptions {
  signal?: AbortSignal;
  pageSize?: number;
  maxPages?: number;
}

export const fetchItems = async (
  baseUrl: string,
  search?: string,
  queryables?: Queryable[],
  concepts?: Concept[],
  bbox?: number[],
  meteogateOnly?: boolean,
  options: FetchPageOptions = {},
): Promise<DiscoveryMetadataItems | null> => {
  if (!baseUrl) {
    return null;
  }
  const url = new URL(`${baseUrl}/items`);
  url.searchParams.set('limit', `${options.pageSize ?? LIMIT}`);
  addQueryParameters(
    url.searchParams,
    search,
    queryables,
    concepts,
    bbox,
    meteogateOnly,
  );
  return fetchItemsWithPagination(url.toString(), options);
};

export const fetchItemsWithPagination = async (
  initialUrl: string,
  options: Omit<FetchPageOptions, 'pageSize'> = {},
): Promise<DiscoveryMetadataItems> => {
  const features: DiscoveryMetadataFeature[] = [];
  const visitedUrls = new Set<string>();
  let nextUrl: string | undefined = initialUrl;
  let numberMatched = 0;
  let firstPage: DiscoveryMetadataItems | undefined;
  let pageCount = 0;

  while (nextUrl) {
    if (pageCount >= (options.maxPages ?? MAX_PAGES)) {
      throw new Error(
        `Records pagination exceeded ${options.maxPages ?? MAX_PAGES} pages`,
      );
    }
    const requestUrl = normalizeRecordsRequestUrl(nextUrl);
    if (visitedUrls.has(requestUrl)) {
      throw new Error(`Records pagination loop detected for ${requestUrl}`);
    }
    visitedUrls.add(requestUrl);

    // Pages must be fetched sequentially because each next URL comes from the prior response.
    // eslint-disable-next-line no-await-in-loop
    const page = (await authFetch(requestUrl, { signal: options.signal }).then(
      handleResponse,
    )) as DiscoveryMetadataItems;
    firstPage ??= page;
    features.push(...(page.features ?? []));
    numberMatched = page.numberMatched;
    pageCount += 1;

    if (Number.isFinite(numberMatched) && features.length >= numberMatched) {
      break;
    }
    const nextLink = page.links?.find((link) => link.rel === 'next');
    nextUrl = nextLink?.href
      ? new URL(nextLink.href, requestUrl).toString()
      : undefined;
  }

  return {
    ...(firstPage ?? { type: 'FeatureCollection', links: [] }),
    features,
    numberMatched: Number.isFinite(numberMatched)
      ? numberMatched
      : features.length,
    numberReturned: features.length,
  };
};

const normalizeRecordsRequestUrl = (href: string): string => {
  const url = new URL(href);
  if (!url.searchParams.has('f')) {
    url.searchParams.set('f', 'json');
  }

  const bbox = url.searchParams.get('bbox');
  if (bbox?.startsWith('[') && bbox.endsWith(']')) {
    try {
      const parsed: unknown = JSON.parse(bbox);
      if (
        Array.isArray(parsed) &&
        parsed.length === 4 &&
        parsed.every(
          (coordinate) =>
            typeof coordinate === 'number' && Number.isFinite(coordinate),
        )
      ) {
        url.searchParams.set('bbox', parsed.join(','));
      }
    } catch {
      return url.toString();
    }
  }

  return url.toString();
};

export const fetchGeoJSON = async (
  collectionUrl: string,
): Promise<FeatureCollection | null> => {
  if (!collectionUrl) {
    return null;
  }
  return authFetch(collectionUrl).then(
    handleResponse,
  ) as Promise<FeatureCollection | null>;
};

export const fetchItem = async (
  baseUrl: string,
  datasetId: string,
): Promise<DiscoveryMetadataFeature | null> => {
  if (!baseUrl || !datasetId) {
    return null;
  }
  return authFetch(`${baseUrl}/items/${datasetId}`).then(
    handleResponse,
  ) as Promise<DiscoveryMetadataFeature | null>;
};

const addQueryParameters = (
  params: URLSearchParams,
  search?: string,
  queryables?: Queryable[],
  concepts?: Concept[],
  bbox?: number[],
  meteogateOnly?: boolean,
): void => {
  if (search !== undefined) {
    params.set('q', search);
  }

  if (queryables !== undefined) {
    queryables.forEach((queryable) => {
      if (queryable.selected && queryable.selected.length > 0) {
        params.set(queryable.id, queryable.selected.join('|'));
      }
    });
  }
  if (concepts !== undefined) {
    const selectedConceptIds = concepts
      .filter((concept) => concept.selected && concept.id !== 'all')
      .map((concept) => concept.id);

    if (selectedConceptIds.length > 0) {
      params.set('themes.concepts.id', selectedConceptIds.join('|'));
    }
  }
  if (bbox !== undefined) {
    if (bbox.length === 4) {
      params.set('bbox', bbox.join(','));
    }
  }
  if (meteogateOnly) {
    params.set('keywords', 'meteogate');
  }
};

export const getDataExplorerConfiguration = async (
  productConfigKey: string,
): Promise<DataExplorerPresets> =>
  fetch(`/assets/${productConfigKey}`).then(
    handleResponse,
  ) as Promise<DataExplorerPresets>;
